import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Respuestas de los endpoints de auth de API v2. Son el contrato OpenAPI que
 * consume el SDK generado (@idc-forms/api-client) y la app móvil: cambiarlas
 * es cambiar el contrato (ver ADR 0015).
 */

/** 202 de POST /v2/auth/client/otp — idéntica exista o no la cuenta. */
export const V2ClientOtpAcceptedSchema = z.object({
  accepted: z.literal(true),
  challengeId: PublicIdSchemas.otpChallenge
})

/** 202 de POST /v2/auth/staff/password-recovery; nunca revela si existe la cuenta. */
export const V2PasswordRecoveryAcceptedSchema = z.object({
  accepted: z.literal(true)
})

/** Sesión del carril móvil: el refresh viaja en el body (sin cookies). */
export const V2ClientSessionTokensSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  sessionId: PublicIdSchemas.session,
  expiresInSeconds: z.number().int().positive()
})

/** Sesión del carril staff: el refresh viaja en cookie httpOnly, nunca en el body. */
export const V2StaffMfaStateSchema = z.object({
  verified: z.boolean(),
  enrolled: z.boolean(),
  required: z.boolean(),
  verifiedAt: InstantSchema.nullable()
})

export const V2StaffSessionTokensSchema = z.object({
  accessToken: z.string().min(1),
  sessionId: PublicIdSchemas.session,
  expiresInSeconds: z.number().int().positive(),
  mfa: V2StaffMfaStateSchema
})

/** Metadatos públicos de una sesión staff; nunca incluye IP, user-agent ni secretos. */
export const V2StaffSessionSchema = z.object({
  id: PublicIdSchemas.session,
  deviceName: z.string().nullable(),
  current: z.boolean(),
  authenticationMethods: z.array(z.string().min(1)),
  mfaVerifiedAt: InstantSchema.nullable(),
  lastUsedAt: InstantSchema.nullable(),
  createdAt: InstantSchema,
  expiresAt: InstantSchema
})

export const V2StaffSessionsPageSchema = z.object({
  data: z.array(V2StaffSessionSchema),
  pagination: V2CursorPaginationSchema
})

/** Perfil self-service del staff: identidad legible sin secretos ni estado interno de Mongo. */
export const V2StaffProfileSchema = z.object({
  id: PublicIdSchemas.user,
  username: z.string().min(1),
  email: z.string().email(),
  fullName: z.string().min(1),
  /** URL del avatar; `null` cuando la cuenta no tiene uno cargado. */
  avatar: z.string().nullable()
})

/**
 * Token puente temporal para endpoints v1 todavía no migrados. No incluye refresh:
 * la sesión canónica y su rotación siguen perteneciendo exclusivamente a v2.
 */
export const V2LegacyAccessTokenSchema = z.object({
  accessToken: z.string().min(1),
  expiresInSeconds: z.literal(15 * 60)
})

/** Principal de seguridad de GET /v2/auth/me (staff, cliente o PAT). */
export const V2PrincipalSchema = z.object({
  kind: z.union([z.literal('staff'), z.literal('client')]),
  id: z.union([PublicIdSchemas.user, PublicIdSchemas.clientAccount]),
  credential: z.union([z.literal('staff_jwt'), z.literal('client_jwt'), z.literal('pat')]),
  credentialId: z.string().min(1),
  sessionId: PublicIdSchemas.session.optional(),
  permissions: z.array(z.string()),
  scopes: z.array(z.string()),
  propertyIds: z.array(PublicIdSchemas.property).optional(),
  mfa: z.boolean(),
  mfaEnrolled: z.boolean().optional(),
  mfaRequired: z.boolean().optional(),
  mfaVerifiedAt: InstantSchema.nullable().optional()
})

/** Inicio de enrolamiento TOTP: el secreto sólo se entrega para configurar el autenticador. */
export const V2MfaEnrollmentSchema = z.object({
  qrCodeDataUrl: z.string().startsWith('data:image/png;base64,'),
  manualEntryKey: z.string().min(16)
})

/** Fin de enrolamiento: sesión elevada y recovery codes visibles una sola vez. */
export const V2MfaEnrollmentCompletedSchema = V2StaffSessionTokensSchema.extend({
  recoveryCodes: z.array(z.string().min(1)).length(10)
})

/** Política MFA efectiva de un usuario staff, sin exponer secretos ni estado interno de Mongo. */
export const V2StaffMfaPolicySchema = z.object({
  userId: PublicIdSchemas.user,
  enrolled: z.boolean(),
  required: z.boolean(),
  requirementSource: z.enum(['account_type', 'administrator', 'none'])
})

/** PAT sin secreto: lo que devuelven listados y el detalle después de crearlo. */
export const V2PersonalAccessTokenSchema = z.object({
  id: PublicIdSchemas.personalAccessToken,
  name: z.string().min(1),
  prefix: z.string().min(1),
  lastFour: z.string().length(4),
  scopes: z.array(z.string()),
  expiresAt: InstantSchema,
  lastUsedAt: InstantSchema.nullable(),
  revokedAt: InstantSchema.nullable(),
  createdAt: InstantSchema
})

/** PAT recién creado o rotado: el valor completo se muestra UNA sola vez. */
export const V2PersonalAccessTokenWithSecretSchema = V2PersonalAccessTokenSchema.extend({
  token: z.string().min(1)
})

/** Vista administrativa: el PAT más su dueño (nunca el secreto). */
export const V2AdminPersonalAccessTokenSchema = V2PersonalAccessTokenSchema.extend({
  owner: z.object({
    id: PublicIdSchemas.user.nullable(),
    name: z.string().nullable(),
    active: z.boolean()
  })
})

/** Cuenta móvil administrada por staff. */
export const V2ClientAccountSchema = z.object({
  id: PublicIdSchemas.clientAccount,
  email: z.string().email(),
  active: z.boolean(),
  blockedAt: InstantSchema.nullable()
})

/** Grant de inmueble otorgado a una cuenta móvil. */
export const V2ClientGrantSchema = z.object({
  id: PublicIdSchemas.accessGrant,
  accountId: PublicIdSchemas.clientAccount,
  propertyId: PublicIdSchemas.property,
  active: z.boolean(),
  /** Vencimiento programado; null = vive hasta revocarse (ADR 0016). */
  expiresAt: InstantSchema.nullable()
})

/** 200 de POST /v2/auth/staff/recovery-codes — códigos de un solo uso. */
export const V2RecoveryCodesSchema = z.object({
  recoveryCodes: z.array(z.string().min(1)).length(10)
})

/**
 * Problem Details (RFC 9457) de API v2: todos los errores salen con
 * Content-Type application/problem+json y un `code` estable para ramificar.
 */
export const V2ProblemDetailsSchema = z.object({
  type: z.string().url(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
  instance: z.string(),
  code: z.string(),
  requestId: z.string(),
  errors: z.unknown().optional()
})

export type V2ClientOtpAccepted = z.infer<typeof V2ClientOtpAcceptedSchema>
export type V2PasswordRecoveryAccepted = z.infer<typeof V2PasswordRecoveryAcceptedSchema>
export type V2ClientSessionTokens = z.infer<typeof V2ClientSessionTokensSchema>
export type V2StaffSessionTokens = z.infer<typeof V2StaffSessionTokensSchema>
export type V2StaffSession = z.infer<typeof V2StaffSessionSchema>
export type V2StaffSessionsPage = z.infer<typeof V2StaffSessionsPageSchema>
export type V2StaffProfile = z.infer<typeof V2StaffProfileSchema>
export type V2LegacyAccessToken = z.infer<typeof V2LegacyAccessTokenSchema>
// "Wire" para no confundirlo con el V2Principal interno del backend (que incluye mongoId).
export type V2WirePrincipal = z.infer<typeof V2PrincipalSchema>
export type V2PersonalAccessToken = z.infer<typeof V2PersonalAccessTokenSchema>
export type V2ProblemDetails = z.infer<typeof V2ProblemDetailsSchema>
